/**
 * Tombstone registry — tiny localStorage-backed guard that hides records
 * the user just deleted, even if a cached/stale list response slips through.
 *
 * Why this exists
 * ───────────────
 * On `portal.lutrom.co.uk` (Cloudflare in front of the FastAPI backend), some
 * users were reporting that Work Orders "come back straight away" after a
 * delete. The server *does* commit the soft-delete (verified via curl) and
 * the frontend already removes the row optimistically. The reappearance
 * happens because the very next GET hits a stale upstream cache and returns
 * the old list, which our re-fetch then writes back into React state.
 *
 * The cache busters (`Cache-Control: no-cache` + `_t=Date.now()` query) help
 * but cannot guarantee a fresh response across every CDN / corporate proxy.
 * Tombstones close the gap: once we've seen a successful 2xx delete, we
 * remember the ID locally for a short window and filter it out of every
 * list payload until either (a) the server proves the row is gone (no
 * tombstone needed) or (b) the user explicitly restores it (Undo).
 *
 * Storage shape
 * ─────────────
 *   localStorage["lutrom_tombstones_v1"] = JSON.stringify({
 *     [scope]: { [id]: <expiresAtMs> }
 *   })
 *
 * Tombstones auto-expire after 24h to prevent permanent ghosts if the user
 * Undoes a delete in another tab/device or an admin restores the row.
 */

const KEY = 'lutrom_tombstones_v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

const safeParse = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const safeWrite = (obj) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    /* quota exceeded or private mode — best effort */
  }
};

const prune = (bucket) => {
  const now = Date.now();
  const out = {};
  for (const [id, exp] of Object.entries(bucket || {})) {
    if (exp > now) out[id] = exp;
  }
  return out;
};

/**
 * Remember that `id` was just deleted in `scope` (e.g. "work_orders").
 * Subsequent calls to `filterTombstoned(scope, list)` will hide it.
 */
export const addTombstone = (scope, id) => {
  if (!scope || !id) return;
  const all = safeParse();
  const bucket = prune(all[scope] || {});
  bucket[id] = Date.now() + TTL_MS;
  all[scope] = bucket;
  safeWrite(all);
};

/**
 * Undo a tombstone, e.g. when the user clicks the Undo toast or restores
 * the record manually. After this the row is allowed to appear in lists
 * again immediately.
 */
export const removeTombstone = (scope, id) => {
  if (!scope || !id) return;
  const all = safeParse();
  const bucket = all[scope] || {};
  if (id in bucket) {
    delete bucket[id];
    all[scope] = bucket;
    safeWrite(all);
  }
};

/**
 * Return the list with tombstoned IDs removed, and side-effect prune any
 * expired entries so the bucket doesn't grow unbounded.
 */
export const filterTombstoned = (scope, list) => {
  if (!Array.isArray(list) || list.length === 0) return list;
  const all = safeParse();
  const bucket = prune(all[scope] || {});
  // Persist pruned bucket so next call is fast.
  if (Object.keys(bucket).length !== Object.keys(all[scope] || {}).length) {
    all[scope] = bucket;
    safeWrite(all);
  }
  if (Object.keys(bucket).length === 0) return list;
  return list.filter((row) => !(row && row.id && bucket[row.id]));
};

/**
 * Read-only view of the active tombstone IDs in a scope (for diagnostics).
 */
export const listTombstones = (scope) => {
  const bucket = prune(safeParse()[scope] || {});
  return Object.keys(bucket);
};
